import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ShoppingBag, Bell, ArrowRight } from 'lucide-react';

const ShopPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-dots opacity-20" />
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Icon */}
            <motion.div
              className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-8"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <ShoppingBag className="w-12 h-12 text-primary" />
            </motion.div>

            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
              </span>
              <span className="text-primary font-medium text-sm">Coming Soon</span>
            </motion.div>

            {/* Heading */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Playmatez <span className="gradient-text">Shop</span>
            </h1>

            {/* Description */}
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Get ready for exclusive sports merchandise, gear, and accessories. 
              Our shop is launching soon with amazing products for every sports enthusiast!
            </p>

            {/* What's Coming */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { label: 'Sports Gear', icon: '🏸' },
                { label: 'Apparel', icon: '👕' },
                { label: 'Accessories', icon: '🎒' },
                { label: 'Equipment', icon: '⚽' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  className="p-4 bg-card border border-white/10 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                >
                  <span className="text-2xl mb-2 block">{item.icon}</span>
                  <span className="text-white text-sm font-medium">{item.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Notify Me Form */}
            <motion.div
              className="max-w-md mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-muted-foreground text-sm mb-4">
                Be the first to know when we launch!
              </p>
              <form 
                className="flex gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = e.target.email.value;
                  window.location.href = `mailto:connect@playmatez.io?subject=Notify me about Playmatez Shop&body=Please notify me at ${email} when the shop launches.`;
                }}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 bg-card border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  data-testid="shop-notify-email"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
                  data-testid="shop-notify-btn"
                >
                  <Bell className="w-4 h-4" />
                  Notify Me
                </button>
              </form>
            </motion.div>

            {/* Back to Home */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Home
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;
